import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  listVoices,
  getAgentConfig,
  updateAgentConfig,
  previewVoice,
} from "adapters/voice-agent.service";

// Sentinel value placed into `error.preview` when the preview endpoint returns 429.
// Exported so UI can check `selectError(state).preview === ERROR_RATE_LIMIT` instead of magic-stringing.
export const ERROR_RATE_LIMIT = "rate_limit";

const initialState = {
  voices: [],
  config: null,
  previewAudio: null,
  // Maps `${libraryId}::${firstMessage}` → { audioBase64, mimeType, renderedText, durationMs }.
  // Populated on previewAgentVoice.fulfilled so repeated previews of the same
  // (voice, message) pair play instantly from memory instead of re-hitting the
  // rate-limited backend. Cleared with clearPreviewCache (or on session reload,
  // since the slice is intentionally not persisted).
  previewCache: {},
  loading: { voices: false, config: false, save: false, preview: false },
  error: { voices: null, config: null, save: null, preview: null },
};

// Builds the cache key for a (voice, firstMessage) pair. The voice object
// carries libraryId plus any overridable tuning params (speed, stability, …).
// Including the tuning params in the key means moving a slider produces a
// cache miss — the user always hears audio that matches their current config.
// Returns null when libraryId or firstMessage is missing.
export const buildPreviewCacheKey = (voice, firstMessage) => {
  if (!voice?.libraryId || !firstMessage) return null;
  const { libraryId, ...rest } = voice;
  // Sort the non-libraryId keys so { speed: 1, stability: 0.5 } and
  // { stability: 0.5, speed: 1 } produce the same key.
  const sortedParams = Object.keys(rest)
    .filter((k) => rest[k] !== undefined && rest[k] !== null)
    .sort()
    .map((k) => `${k}=${rest[k]}`)
    .join("&");
  return `${libraryId}::${sortedParams}::${firstMessage}`;
};

const extractError = (err) => {
  if (err?.response?.status === 429) return ERROR_RATE_LIMIT;
  return err?.response?.data?.message || err?.message || "Unknown error";
};

// Splits a thunk argument into the request body and routing metadata.
// Any key prefixed with `_internalAgentId` is treated as routing metadata
// (extracted, not sent to the server). All other keys flow through as the body.
// Callers:
//   dispatch(saveAgentConfig({ voice, firstMessage }))                       → uses BARI by default
//   dispatch(saveAgentConfig({ voice, firstMessage, _internalAgentId: 'x' })) → targets a custom agent
// A forgotten underscore (e.g. passing `internalAgentId` plain) would silently
// get sent to the server as part of the body; the underscore convention makes
// the routing intent explicit at every call site.
const splitBody = (arg) => {
  const { _internalAgentId, ...body } = arg || {};
  return { body, internalAgentId: _internalAgentId };
};

export const fetchVoices = createAsyncThunk(
  "voiceAgent/fetchVoices",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const data = await listVoices(filters);
      return data.voices || [];
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  }
);

export const fetchAgentConfig = createAsyncThunk(
  "voiceAgent/fetchAgentConfig",
  async (internalAgentId, { rejectWithValue }) => {
    try {
      return await getAgentConfig(internalAgentId);
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  }
);

export const saveAgentConfig = createAsyncThunk(
  "voiceAgent/saveAgentConfig",
  async (arg, { rejectWithValue }) => {
    try {
      const { body, internalAgentId } = splitBody(arg);
      return await updateAgentConfig(body, internalAgentId);
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  }
);

export const previewAgentVoice = createAsyncThunk(
  "voiceAgent/previewAgentVoice",
  async (arg, { rejectWithValue }) => {
    try {
      const { body, internalAgentId } = splitBody(arg);
      return await previewVoice(body, internalAgentId);
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  }
);

const voiceAgentSlice = createSlice({
  name: "voiceAgent",
  initialState,
  reducers: {
    clearPreviewAudio: (state) => {
      state.previewAudio = null;
      state.error.preview = null;
    },
    clearPreviewCache: (state) => {
      state.previewCache = {};
    },
    // Used by the streaming preview path — after the MSE player finishes
    // assembling the chunked response, it writes the full audio into the cache
    // here so a repeated preview of the same (voice + params, firstMessage)
    // plays instantly without re-streaming. The caller passes the full voice
    // form-value (libraryId + overridable tuning params) so the key includes
    // the params and tuning tweaks naturally miss the cache.
    cachePreview: (state, action) => {
      const { voice, firstMessage, audioBase64, mimeType } =
        action.payload || {};
      const key = buildPreviewCacheKey(voice, firstMessage);
      if (!key || !audioBase64) return;
      state.previewCache[key] = {
        audioBase64,
        mimeType: mimeType || "audio/mpeg",
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVoices.pending, (state) => {
        state.loading.voices = true;
        state.error.voices = null;
      })
      .addCase(fetchVoices.fulfilled, (state, action) => {
        state.loading.voices = false;
        state.voices = action.payload;
      })
      .addCase(fetchVoices.rejected, (state, action) => {
        state.loading.voices = false;
        state.error.voices = action.payload || "Unknown error";
      })

      .addCase(fetchAgentConfig.pending, (state) => {
        state.loading.config = true;
        state.error.config = null;
      })
      .addCase(fetchAgentConfig.fulfilled, (state, action) => {
        state.loading.config = false;
        state.config = action.payload;
      })
      .addCase(fetchAgentConfig.rejected, (state, action) => {
        state.loading.config = false;
        state.error.config = action.payload || "Unknown error";
      })

      .addCase(saveAgentConfig.pending, (state) => {
        state.loading.save = true;
        state.error.save = null;
      })
      .addCase(saveAgentConfig.fulfilled, (state, action) => {
        state.loading.save = false;
        state.config = action.payload;
      })
      .addCase(saveAgentConfig.rejected, (state, action) => {
        state.loading.save = false;
        state.error.save = action.payload || "Unknown error";
      })

      .addCase(previewAgentVoice.pending, (state) => {
        state.loading.preview = true;
        state.error.preview = null;
        state.previewAudio = null;
      })
      .addCase(previewAgentVoice.fulfilled, (state, action) => {
        state.loading.preview = false;
        state.previewAudio = action.payload;
        // Cache so repeated previews of the same (voice + tuning params,
        // message) skip the network round-trip. action.meta.arg is the
        // original payload passed to the thunk; arg.voice carries libraryId
        // alongside any tuning params the user adjusted.
        const arg = action.meta?.arg || {};
        const cacheKey = buildPreviewCacheKey(arg.voice, arg.firstMessage);
        if (cacheKey) {
          state.previewCache[cacheKey] = action.payload;
        }
      })
      .addCase(previewAgentVoice.rejected, (state, action) => {
        state.loading.preview = false;
        state.error.preview = action.payload || "Unknown error";
      });
  },
});

export const { clearPreviewAudio, clearPreviewCache, cachePreview } =
  voiceAgentSlice.actions;

export const selectVoices = (s) => s.voiceAgent.voices;
export const selectAgentConfig = (s) => s.voiceAgent.config;
export const selectPreviewAudio = (s) => s.voiceAgent.previewAudio;
export const selectPreviewCache = (s) => s.voiceAgent.previewCache;
export const selectLoading = (s) => s.voiceAgent.loading;
export const selectError = (s) => s.voiceAgent.error;

export default voiceAgentSlice.reducer;
